import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Tabs,
  Tab,
  Stack,
  Fade,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  CorporateFare as CorporateFareIcon,
  Palette as PaletteIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import AdvancedColorPicker from './AdvancedColorPicker';
import { buildThemeColorsFromPrimary, contrastText } from '../utils/colorMath';

const THEME_META: Record<
  string,
  { primary: string; secondary: string; light: string; name: string; description: string; badge?: string }
> = {
  microsoft: {
    primary: '#0078d4',
    secondary: '#106ebe',
    light: '#c7e0f4',
    name: 'Microsoft',
    description: 'Fluent enterprise blue',
    badge: 'Default',
  },
  linkedin: {
    primary: '#0a66c2',
    secondary: '#0073b1',
    light: '#c0d9e8',
    name: 'LinkedIn',
    description: 'Professional navy',
  },
  turquoise: {
    primary: '#20B2AA',
    secondary: '#40E0D0',
    light: '#AFEEEE',
    name: 'Turquoise',
    description: 'Modern teal accent',
  },
  blue: {
    primary: '#1976d2',
    secondary: '#42a5f5',
    light: '#bbdefb',
    name: 'Classic Blue',
    description: 'Material primary',
  },
  purple: {
    primary: '#9c27b0',
    secondary: '#ba68c8',
    light: '#e1bee7',
    name: 'Purple',
    description: 'Bold violet',
  },
  green: {
    primary: '#4caf50',
    secondary: '#81c784',
    light: '#c8e6c9',
    name: 'Green',
    description: 'Fresh emerald',
  },
  orange: {
    primary: '#ff9800',
    secondary: '#ffb74d',
    light: '#ffe0b2',
    name: 'Orange',
    description: 'Warm energy',
  },
};

type PreviewColors = {
  primary: string;
  secondary: string;
  light: string;
  contrastText: string;
};

const ThemePreview: React.FC<{ colors: PreviewColors; isDark: boolean; label: string }> = ({
  colors,
  isDark,
  label,
}) => {
  const surface = isDark ? '#151a22' : '#f8fafc';
  const paper = isDark ? '#1c2330' : '#ffffff';
  const text = isDark ? '#e8eaed' : '#0f172a';
  const muted = isDark ? '#9aa3b2' : '#64748b';

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: surface,
        boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.35)' : '0 12px 32px rgba(15,23,42,0.08)',
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.1,
          bgcolor: colors.primary,
          color: colors.contrastText,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 0.2 }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.55)' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.55)' }} />
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.22)',
              border: '1px solid rgba(255,255,255,0.35)',
            }}
          />
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', minHeight: 148 }}>
        <Box
          sx={{
            width: 44,
            bgcolor: paper,
            borderRight: '1px solid',
            borderColor: 'divider',
            py: 1.25,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 22,
                height: 22,
                borderRadius: 1,
                bgcolor: i === 0 ? `${colors.primary}22` : 'transparent',
                color: i === 0 ? colors.primary : muted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {i + 1}
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1, p: 1.5 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: text, mb: 0.75 }}>
            Workspace preview
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: muted, mb: 1.25, lineHeight: 1.4 }}>
            Buttons, chips, and chrome update instantly with your brand color.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
            <Box
              sx={{
                px: 1.25,
                py: 0.55,
                borderRadius: 1,
                bgcolor: colors.primary,
                color: colors.contrastText,
                fontSize: '0.68rem',
                fontWeight: 700,
              }}
            >
              Primary
            </Box>
            <Box
              sx={{
                px: 1.25,
                py: 0.55,
                borderRadius: 1,
                border: `1px solid ${colors.primary}`,
                color: colors.primary,
                fontSize: '0.68rem',
                fontWeight: 700,
                bgcolor: paper,
              }}
            >
              Outline
            </Box>
            <Chip
              label="Active"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: colors.light,
                color: colors.primary,
              }}
            />
          </Stack>
          <Box
            sx={{
              height: 8,
              borderRadius: 99,
              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ width: '62%', height: '100%', bgcolor: colors.primary }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ThemeSelector: React.FC = () => {
  const { currentTheme, setTheme, availableThemes, colors, setCustomColors, resolvedMode } = useTheme();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [draftHex, setDraftHex] = useState(colors.primary);

  const isDark = resolvedMode === 'dark';

  const preview = useMemo<PreviewColors>(() => {
    if (tab === 1) {
      const built = buildThemeColorsFromPrimary(draftHex);
      return {
        primary: built.primary,
        secondary: built.secondary,
        light: built.light,
        contrastText: built.contrastText,
      };
    }
    return {
      primary: colors.primary,
      secondary: colors.secondary,
      light: colors.light,
      contrastText: colors.contrastText || contrastText(colors.primary),
    };
  }, [tab, draftHex, colors]);

  if (!user || !['super_admin', 'sso_admin', 'grc_admin'].includes(user.role)) {
    return null;
  }

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const handleThemeChange = async (theme: string) => {
    setSaving(true);
    setSaved(false);
    await setTheme(theme as any);
    setSaving(false);
    flashSaved();
  };

  const handleApplyCustom = async (palette: ReturnType<typeof buildThemeColorsFromPrimary>) => {
    setSaving(true);
    setSaved(false);
    await setCustomColors(palette);
    setSaving(false);
    flashSaved();
  };

  const openDialog = () => {
    setDraftHex(colors.primary);
    setTab(currentTheme === 'custom' ? 1 : 0);
    setOpen(true);
  };

  const activeMeta =
    currentTheme === 'custom'
      ? { name: 'Custom', primary: colors.primary }
      : THEME_META[currentTheme] || { name: String(currentTheme), primary: colors.primary };

  return (
    <>
      <Tooltip title="Organization Theme">
        <IconButton color="inherit" onClick={openDialog} size="small" aria-label="Organization theme">
          <CorporateFareIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            background: isDark
              ? `linear-gradient(135deg, ${preview.primary}33 0%, transparent 70%)`
              : `linear-gradient(135deg, ${preview.primary}18 0%, transparent 70%)`,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 1.5,
                    bgcolor: preview.primary,
                    color: preview.contrastText,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, letterSpacing: -0.2 }}>
                    Organization Theme
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    Active: <strong>{activeMeta.name}</strong> · applies to everyone in your org
                  </Typography>
                </Box>
              </Stack>
            </Box>
            <IconButton onClick={() => setOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 36,
              mt: 1,
              '& .MuiTab-root': {
                minHeight: 36,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.82rem',
                px: 1.5,
              },
            }}
          >
            <Tab icon={<PaletteIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Presets" />
            <Tab icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Custom" />
          </Tabs>
        </Box>

        <DialogContent sx={{ pt: 2.5, pb: 1.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
              gap: 2.5,
              alignItems: 'start',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                Live preview
              </Typography>
              <ThemePreview
                colors={preview}
                isDark={isDark}
                label={tab === 1 ? 'Custom brand' : activeMeta.name}
              />

              <Fade in={saved}>
                <Alert severity="success" sx={{ mt: 1.5, fontSize: '0.78rem', py: 0.25 }}>
                  Theme saved for your organization.
                </Alert>
              </Fade>
            </Box>

            <Box>
              {tab === 0 ? (
                <>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    Curated palettes
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1.25,
                    }}
                  >
                    {availableThemes.map((theme) => {
                      const meta = THEME_META[theme as keyof typeof THEME_META];
                      if (!meta) return null;
                      const isSelected = currentTheme === theme;
                      return (
                        <Box
                          key={theme}
                          component="button"
                          type="button"
                          disabled={saving}
                          onClick={() => handleThemeChange(theme)}
                          sx={{
                            textAlign: 'left',
                            cursor: saving ? 'default' : 'pointer',
                            border: '1px solid',
                            borderColor: isSelected ? meta.primary : 'divider',
                            borderRadius: 2,
                            p: 1.25,
                            bgcolor: isSelected
                              ? isDark
                                ? `${meta.primary}22`
                                : `${meta.primary}10`
                              : 'background.paper',
                            boxShadow: isSelected ? `0 0 0 2px ${meta.primary}33` : 'none',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
                            '&:hover': saving
                              ? {}
                              : {
                                  borderColor: meta.primary,
                                  transform: 'translateY(-1px)',
                                },
                          }}
                        >
                          <Box
                            sx={{
                              height: 44,
                              borderRadius: 1.5,
                              mb: 1,
                              background: `linear-gradient(135deg, ${meta.primary} 0%, ${meta.secondary} 55%, ${meta.light} 100%)`,
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            {isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 6,
                                  right: 6,
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  bgcolor: '#fff',
                                  color: meta.primary,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                }}
                              >
                                <CheckIcon sx={{ fontSize: 14 }} />
                              </Box>
                            )}
                          </Box>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 750 }}>{meta.name}</Typography>
                            {meta.badge && (
                              <Chip
                                label={meta.badge}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  bgcolor: `${meta.primary}18`,
                                  color: meta.primary,
                                }}
                              />
                            )}
                          </Stack>
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
                            {meta.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    Custom brand color
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.75rem', py: 0.25 }}>
                    Drag the canvas or enter hex/RGB. Secondary and light shades are generated automatically.
                  </Alert>
                  <AdvancedColorPicker
                    value={draftHex}
                    onChange={setDraftHex}
                    showApply
                    onApply={handleApplyCustom}
                  />
                </>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ mr: 'auto', fontSize: '0.72rem', color: 'text.secondary' }}>
            Changes apply org-wide immediately
          </Typography>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: preview.primary,
              color: preview.contrastText,
              '&:hover': { bgcolor: preview.primary, filter: 'brightness(0.92)' },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThemeSelector;
